import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthIndustryTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService } from '../catalog/catalog.service';
import { InsuranceApiService } from '../insurance/insurance-api.service';
import { CommerceOrderSelectorComponent } from '../../shared/design-system/commerce-order-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  mergeStructuredJsonPayload,
  parseStructuredJsonPayload
} from '../../shared/design-system/structured-json-form.utils';
import { GrowthAdminService, GrowthIndustryPackResponse, IndustryBindingResponse } from './growth-admin.service';
import {
  GROWTH_INDUSTRY_BINDING_ENTITY_TYPES,
  emptyIndustryBindingMetadataJson,
  growthIndustryBindingMetadataFields
} from './growth-industry-form.models';

type EntityOption = { label: string; value: string };

@Component({
  standalone: true,
  selector: 'ain-growth-industry-binding-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    SelectModule,
    PartySelectorComponent,
    CustomerSelectorComponent,
    CommerceOrderSelectorComponent,
    FinancialAccountSelectorComponent,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('growth.industryBindings.detail.form.createTitle') : t('growth.industryBindings.detail.form.viewTitle')"
      [description]="t('growth.industryBindings.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.industryBindings.detail.help.title'"
        [descriptionKey]="'growth.industryBindings.detail.help.description'"
        [bulletKeys]="['growth.industryBindings.detail.help.bullets.entity', 'growth.industryBindings.detail.help.bullets.metadata']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/marketplace/bindings']">{{ t('catalog.action.backToList') }}</a>
          @if (isCreateMode()) {
            <button pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="save()"></button>
          }
        </div>

        <section class="card">
          <h3>{{ t('growth.industryBindings.detail.sections.details') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.industryBindings.detail.help.details.title'"
            [descriptionKey]="'growth.industryBindings.detail.help.details.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.industryBindings.columns.industryKey') }}</span>
              <p-select
                class="field-select"
                [options]="industryKeyOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="form.industryKey"
                [disabled]="!isCreateMode()"
                appendTo="body"
                [placeholder]="t('growth.industryBindings.detail.fields.industryKeyPlaceholder')" />
            </label>
            <label>
              <span>{{ t('growth.industryBindings.columns.entityType') }}</span>
              <p-select
                class="field-select"
                [options]="entityTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="form.entityType"
                (ngModelChange)="onEntityTypeChange($event)"
                [disabled]="!isCreateMode()"
                appendTo="body" />
            </label>
            <label class="wide">
              <span>{{ t('growth.industryBindings.columns.entityId') }}</span>
              @switch (form.entityType) {
                @case ('Customer') {
                  <ain-customer-selector
                    [(ngModel)]="form.entityId"
                    [disabled]="!isCreateMode()" />
                }
                @case ('Order') {
                  <ain-commerce-order-selector
                    [(ngModel)]="form.entityId"
                    [disabled]="!isCreateMode()" />
                }
                @case ('Account') {
                  <ain-financial-account-selector
                    [(ngModel)]="form.entityId"
                    [disabled]="!isCreateMode()" />
                }
                @case ('Guest') {
                  <ain-party-selector
                    [ngModel]="form.entityId"
                    (ngModelChange)="onPartyEntitySelected($event)"
                    [disabled]="!isCreateMode()" />
                }
                @case ('Student') {
                  <ain-party-selector
                    [ngModel]="form.entityId"
                    (ngModelChange)="onPartyEntitySelected($event)"
                    [disabled]="!isCreateMode()" />
                }
                @case ('Patient') {
                  <ain-party-selector
                    [ngModel]="form.entityId"
                    (ngModelChange)="onPartyEntitySelected($event)"
                    [disabled]="!isCreateMode()" />
                }
                @case ('CitizenCase') {
                  <ain-party-selector
                    [ngModel]="form.entityId"
                    (ngModelChange)="onPartyEntitySelected($event)"
                    [disabled]="!isCreateMode()" />
                }
                @default {
                  <p-select
                    class="field-select"
                    [options]="entityOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="form.entityId"
                    [disabled]="!isCreateMode() || entityOptionsLoading()"
                    [loading]="entityOptionsLoading()"
                    [filter]="true"
                    filterBy="label,value"
                    [showClear]="true"
                    appendTo="body"
                    [placeholder]="t('growth.industryBindings.detail.fields.entityPlaceholder')" />
                }
              }
            </label>
            <label class="wide">
              <span>{{ t('growth.industryBindings.columns.partyId') }}</span>
              @if (isCreateMode()) {
                <ain-party-selector [(ngModel)]="form.partyId" />
              } @else {
                <ain-party-selector [ngModel]="form.partyId" [disabled]="true" />
              }
            </label>
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.industryBindings.detail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.industryBindings.detail.help.metadata.title'"
            [descriptionKey]="'growth.industryBindings.detail.help.metadata.description'" />
          @if (isCreateMode()) {
            <ain-structured-json-form
              [fields]="metadataFields"
              [(payloadJson)]="metadataFormJson" />
          } @else {
            <ain-structured-json-display
              [fields]="metadataFields"
              [payloadJson]="binding()?.metadataJson ?? '{}'" />
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 3; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .message.error { color: #b91c1c; }
    @media (max-width: 960px) {
      .grid { grid-template-columns: 1fr 1fr; }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 640px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class GrowthIndustryBindingDetailComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly catalogApi = inject(CatalogService);
  private readonly insuranceApi = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly binding = signal<IndustryBindingResponse | null>(null);
  readonly packOptions = signal<GrowthIndustryPackResponse[]>([]);
  readonly entityOptions = signal<EntityOption[]>([]);
  readonly entityOptionsLoading = signal(false);

  readonly metadataFields = growthIndustryBindingMetadataFields;

  metadataFormJson = emptyIndustryBindingMetadataJson();

  form = {
    industryKey: '',
    entityType: 'Customer' as string,
    entityId: null as string | null,
    partyId: null as string | null
  };

  private bindingId = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.bindingId = this.route.snapshot.paramMap.get('bindingId') ?? '';
    this.isCreateMode.set(!this.bindingId || this.bindingId === 'new');

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthIndustryChanged || this.isCreateMode()) {
            return false;
          }
          return true;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadIndustryKeys();

    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.load(true);
  }

  industryKeyOptions(): Array<{ label: string; value: string }> {
    return this.packOptions().map(pack => ({
      value: pack.key,
      label: `${pack.displayName} (${pack.key})`
    }));
  }

  entityTypeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_INDUSTRY_BINDING_ENTITY_TYPES.map(value => ({
      value,
      label: this.t(`growth.industryBindings.entityType.${value}`)
    }));
  }

  onEntityTypeChange(entityType: string): void {
    this.form.entityType = entityType;
    this.form.entityId = null;
    this.loadEntityOptions(entityType);
  }

  onPartyEntitySelected(partyId: string | null): void {
    this.form.entityId = partyId;
    if (partyId && !this.form.partyId) {
      this.form.partyId = partyId;
    }
  }

  save(): void {
    if (!this.form.industryKey.trim()) {
      this.errorMessage.set(this.t('growth.industryBindings.detail.messages.industryKeyRequired'));
      return;
    }
    if (!this.form.entityId?.trim()) {
      this.errorMessage.set(this.t('growth.industryBindings.detail.messages.entityIdRequired'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.api.createBinding({
      industryKey: this.form.industryKey.trim(),
      entityType: this.form.entityType,
      entityId: this.form.entityId.trim(),
      partyId: this.form.partyId,
      metadataJson: mergeStructuredJsonPayload(
        this.metadataFields,
        emptyIndustryBindingMetadataJson(),
        parseStructuredJsonPayload(this.metadataFormJson)
      )
    }).pipe(finalize(() => this.saving.set(false))).subscribe({
      next: created => {
        void this.router.navigate(['/growth/marketplace/bindings', created.id]);
      },
      error: () => this.errorMessage.set(this.t('growth.industryBindings.detail.messages.saveFailed'))
    });
  }

  load(showLoading = true): void {
    if (!this.bindingId || this.bindingId === 'new') {
      this.loading.set(false);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getBinding(this.bindingId).subscribe({
      next: row => {
        this.binding.set(row);
        this.populateForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.binding.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.industryBindings.detail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private populateForm(row: IndustryBindingResponse): void {
    this.form = {
      industryKey: row.industryKey,
      entityType: row.entityType,
      entityId: row.entityId,
      partyId: row.partyId
    };
    this.metadataFormJson = row.metadataJson?.trim() ? row.metadataJson : emptyIndustryBindingMetadataJson();
    this.loadEntityOptions(row.entityType);
  }

  private loadIndustryKeys(): void {
    this.api.listIndustryPacks().subscribe({
      next: packs => this.packOptions.set(packs),
      error: () => this.packOptions.set([])
    });
  }

  private loadEntityOptions(entityType: string): void {
    if (entityType === 'Customer' || entityType === 'Order' || entityType === 'Account'
      || entityType === 'Guest' || entityType === 'Student' || entityType === 'Patient' || entityType === 'CitizenCase') {
      this.entityOptions.set([]);
      this.entityOptionsLoading.set(false);
      return;
    }

    this.entityOptionsLoading.set(true);
    this.entityOptions.set([]);

    if (entityType === 'Policy') {
      this.insuranceApi.listPolicies().subscribe({
        next: items => {
          this.entityOptions.set(items.map(item => ({
            value: item.id,
            label: `${item.policyNumber} — ${item.productName}`
          })));
          this.entityOptionsLoading.set(false);
        },
        error: () => {
          this.entityOptions.set([]);
          this.entityOptionsLoading.set(false);
        }
      });
      return;
    }

    if (entityType === 'Room') {
      this.catalogApi.listHospitalityRooms().subscribe({
        next: items => {
          this.entityOptions.set(items.map(item => ({
            value: item.id,
            label: item.floor ? `${item.roomNumber} (${item.floor})` : item.roomNumber
          })));
          this.entityOptionsLoading.set(false);
        },
        error: () => {
          this.entityOptions.set([]);
          this.entityOptionsLoading.set(false);
        }
      });
      return;
    }

    if (entityType === 'Subscriber') {
      this.catalogApi.listProductSubscriptionContracts().subscribe({
        next: items => {
          this.entityOptions.set(items.map(item => ({
            value: item.id,
            label: `${item.customerReference} — ${item.status}`
          })));
          this.entityOptionsLoading.set(false);
        },
        error: () => {
          this.entityOptions.set([]);
          this.entityOptionsLoading.set(false);
        }
      });
      return;
    }

    this.entityOptionsLoading.set(false);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIndustryTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
